import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, ChallengeStatus } from '@/api/generated';
import { getMfaChallengeStatus } from '@/services/apis/mfa-orchestrator.api';
import type {
  MfaPollingState,
  UseMfaPollingProps,
  UseMfaPollingReturn,
} from './use-mfa-polling.types';
import { DEFAULT_MFA_POLLING_CONFIG } from './use-mfa-polling.types';

// The user decided: approving or declining ends the challenge for good.
const DECIDED_STATUSES: ChallengeStatus[] = [
  ChallengeStatus.APPROVED,
  ChallengeStatus.REJECTED,
];

const MAX_RETRIES_ERROR_MESSAGE = 'Maximum polling attempts exceeded';
const SESSION_EXPIRED_ERROR_MESSAGE = 'MFA session expired';

/**
 * Hook for polling MFA challenge status
 * Only responsible for polling and reporting status changes
 * Does NOT handle orchestrator submission - that's the parent's responsibility
 */
export const useMfaPolling = ({
  pollingInterval = DEFAULT_MFA_POLLING_CONFIG.pollingInterval,
  maxRetries = DEFAULT_MFA_POLLING_CONFIG.maxRetries,
  onStatusChange,
  onError,
}: UseMfaPollingProps = {}): UseMfaPollingReturn => {
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const isMountedRef = useRef(true);
  // The caller's start effect re-runs on the resolve transition; without this
  // latch it re-polls a challenge whose session the backend already tore down.
  // Only a user decision latches — FAILED stays retryable.
  const hasResolvedRef = useRef(false);

  const [state, setState] = useState<MfaPollingState>({
    status: ChallengeStatus.PENDING,
    isPolling: false,
    errorMessage: '',
    retryCount: 0,
  });

  /**
   * Stop polling
   */
  const stopPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    setState(prev => ({ ...prev, isPolling: false }));
  }, []);

  /**
   * Poll for challenge status
   */
  const pollStatus = useCallback(async () => {
    if (!pollingIntervalRef.current) {
      return;
    }

    try {
      const response = await getMfaChallengeStatus();
      const newStatus = response.status;

      if (!isMountedRef.current) {
        return;
      }

      // Update status if changed
      if (newStatus && newStatus !== state.status) {
        setState(prev => ({
          ...prev,
          status: newStatus,
          errorMessage: '',
        }));

        // Notify parent of status change
        onStatusChange?.(newStatus);
      }

      // Keyed off the response, not the diff: a stale closure can report the
      // same resolved status twice and must still stop.
      if (newStatus && newStatus !== ChallengeStatus.PENDING) {
        hasResolvedRef.current = DECIDED_STATUSES.includes(newStatus);
        stopPolling();
      }
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }

      // 401 = MFA session gone; a reload would mint a new push (SPRK-6475)
      if (error instanceof ApiError && error.status === 401) {
        stopPolling();
        onError?.(new Error(SESSION_EXPIRED_ERROR_MESSAGE));
        setState(prev => ({
          ...prev,
          errorMessage: SESSION_EXPIRED_ERROR_MESSAGE,
        }));

        return;
      }

      setState(prev => {
        const newRetryCount = prev.retryCount + 1;

        // Stop after max retries
        if (newRetryCount > maxRetries) {
          stopPolling();

          const errorMessage = MAX_RETRIES_ERROR_MESSAGE;
          const errorObj = new Error(errorMessage);
          onError?.(errorObj);

          return {
            ...prev,
            retryCount: newRetryCount,
            errorMessage,
            isPolling: false,
          };
        }

        return {
          ...prev,
          retryCount: newRetryCount,
        };
      });
    }
  }, [state.status, maxRetries, onStatusChange, onError, stopPolling]);

  /**
   * Start polling
   */
  const startPolling = useCallback(() => {
    if (pollingIntervalRef.current || hasResolvedRef.current) {
      return;
    }

    setState(prev => ({
      ...prev,
      isPolling: true,
      errorMessage: '',
      retryCount: 0,
    }));

    // Initial poll
    void pollStatus();

    // Set up interval
    pollingIntervalRef.current = setInterval(pollStatus, pollingInterval);
  }, [pollStatus, pollingInterval]);

  /**
   * Reset polling state
   */
  const resetPolling = useCallback(() => {
    hasResolvedRef.current = false;
    stopPolling();
    setState({
      status: ChallengeStatus.PENDING,
      isPolling: false,
      errorMessage: '',
      retryCount: 0,
    });
  }, [stopPolling]);

  /**
   * Retry polling after error
   */
  const retry = useCallback(() => {
    // A decided challenge cannot be revived; resetting would clear the terminal
    // card and leave a pending UI that nothing polls.
    if (hasResolvedRef.current) {
      return;
    }

    resetPolling();
    startPolling();
  }, [resetPolling, startPolling]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  return {
    state,
    startPolling,
    stopPolling,
    retry,
    resetPolling,
  };
};
